import "../../candidate-intelligence/results/styles.css";
import type {
  EvaluationSetDetail,
  EvaluationSetItemView,
} from "@shared/labs/level-assessment-evaluation-set";
import { ArrowDownUp, Download } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useRoute } from "wouter";
import { rpcClient } from "@/lib/rpc";
import { C, DM, UI } from "../results/ui-tokens";
import { CandidateDrawer } from "./candidate-drawer";
import { LevelCell, MiniBars } from "./cells";
import { ProfileDrawer } from "./profile-drawer";
import { initials, Muted, reviewChipProps, statusChipProps } from "./ui";

const POLL_MS = 4000;

/** Sort key: overall level, or a specific competency axis id. */
type SortKey = "level" | { axisId: string };

function levelOf(e: EvaluationSetItemView, key: SortKey): number | null {
  if (key === "level") return e.overallLevel;
  return e.perAxis[key.axisId]?.level ?? null;
}

export default function LevelAssessmentSetView() {
  const [, params] = useRoute("/labs/level-assessment/sets/:setId");
  const setId = params?.setId ?? "";

  const [data, setData] = useState<EvaluationSetDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("level");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Two independent surfaces: clicking a candidate opens the profile résumé;
  // the "Revisar" button opens the per-competency review drawer.
  const [profileEvalId, setProfileEvalId] = useState<string | null>(null);
  const [drawerEvalId, setDrawerEvalId] = useState<string | null>(null);
  const pollRef = useRef<{ cancelled: boolean } | null>(null);

  const loadSet = useCallback(
    async (opts?: { silent?: boolean }): Promise<EvaluationSetDetail | null> => {
      if (!opts?.silent) setLoading(true);
      try {
        const res = (await rpcClient.labs.levelAssessmentSetGet({ setId })) as EvaluationSetDetail;
        setData(res);
        setError(null);
        return res;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erro ao carregar o pacote.");
        return null;
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [setId],
  );

  useEffect(() => {
    if (setId) void loadSet();
  }, [setId, loadSet]);

  // Poll while the batch is still evaluating; stop as soon as it's ready.
  useEffect(() => {
    if (data?.set.status !== "evaluating") return;
    const token = { cancelled: false };
    pollRef.current = token;
    const id = setInterval(() => {
      void (async () => {
        const res = await loadSet({ silent: true });
        if (token.cancelled || !res) return;
        if (res.set.status !== "evaluating") clearInterval(id);
      })();
    }, POLL_MS);
    return () => {
      token.cancelled = true;
      clearInterval(id);
    };
  }, [data?.set.status, loadSet]);

  const sorted = useMemo(() => {
    if (!data) return [];
    const rows = [...data.evaluations];
    rows.sort((a, b) => {
      const la = levelOf(a, sortKey);
      const lb = levelOf(b, sortKey);
      // Abstained (null) always sorts last, regardless of direction.
      if (la == null && lb == null) return 0;
      if (la == null) return 1;
      if (lb == null) return -1;
      return sortDir === "asc" ? la - lb : lb - la;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  const crossAxes = useMemo(() => data?.axes.filter((a) => a.scope === "cross") ?? [], [data]);
  const vertAxes = useMemo(() => data?.axes.filter((a) => a.scope === "vertical") ?? [], [data]);

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function exportCsv() {
    if (!data) return;
    const rows = selected.size > 0 ? sorted.filter((e) => selected.has(e.evaluationId)) : sorted;
    const header = ["Candidato", "Nível geral", ...data.axes.map((a) => a.label), "Revisão"];
    const lines = rows.map((e) => {
      const cells = [
        e.candidateName,
        e.overallLevel == null ? "" : `L${e.overallLevel}`,
        ...data.axes.map((a) => {
          const lv = e.perAxis[a.axisId]?.level;
          return lv == null ? "" : `L${lv}`;
        }),
        reviewChipProps(e.reviewStatus).label,
      ];
      return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",");
    });
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.set.name.replace(/[^\w-]+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const evaluatedCount = data?.evaluations.length ?? 0;

  return (
    <div className="ci-stripe ci-stripe-page">
      <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 1180 }}>
        <Link
          href="/labs/level-assessment/sets"
          style={{
            ...UI,
            fontSize: 12,
            color: C.ink50,
            textDecoration: "none",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          ← Pacotes de candidatos
        </Link>

        {error && (
          <div style={{ marginTop: 12 }}>
            <Muted>Falha ao carregar o pacote: {error}</Muted>
          </div>
        )}
        {loading && !data && (
          <div style={{ marginTop: 12 }}>
            <Muted>Carregando pacote…</Muted>
          </div>
        )}

        {data && (
          <>
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 16,
                margin: "10px 0 18px",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <h1
                    style={{
                      ...DM,
                      margin: 0,
                      fontSize: 28,
                      fontWeight: 700,
                      letterSpacing: "-0.015em",
                      color: C.ink,
                    }}
                  >
                    {data.set.name}
                  </h1>
                  {(() => {
                    const s = statusChipProps(data.set.status);
                    return (
                      <span
                        style={{
                          ...UI,
                          fontSize: 11.5,
                          fontWeight: 600,
                          color: s.fg,
                          background: s.bg,
                          padding: "3px 9px",
                          borderRadius: 999,
                        }}
                      >
                        {s.label}
                      </span>
                    );
                  })()}
                </div>
                <div style={{ ...UI, fontSize: 13, color: C.ink50 }}>
                  {data.set.note ? `${data.set.note} · ` : ""}
                  {data.set.status === "evaluating"
                    ? `avaliando ${evaluatedCount} de ${data.set.expectedCount} candidatos…`
                    : `${evaluatedCount} candidatos`}
                </div>
              </div>
              <button
                type="button"
                onClick={exportCsv}
                style={{
                  ...DM,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  border: `1px solid ${C.ink10}`,
                  cursor: "pointer",
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: 14,
                  padding: "8px 16px",
                  background: C.white,
                  color: C.ink,
                }}
              >
                <Download size={16} />
                Exportar CSV
              </button>
            </div>

            {/* Toolbar — sort */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span
                style={{
                  ...UI,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  color: C.ink50,
                }}
              >
                Ordenar por
              </span>
              <select
                value={sortKey === "level" ? "level" : sortKey.axisId}
                onChange={(e) =>
                  setSortKey(e.target.value === "level" ? "level" : { axisId: e.target.value })
                }
                style={{
                  ...UI,
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.ink,
                  background: C.white,
                  border: `1px solid ${C.ink10}`,
                  borderRadius: 8,
                  padding: "7px 12px",
                  cursor: "pointer",
                }}
              >
                <option value="level">Nível geral</option>
                {data.axes.map((a) => (
                  <option key={a.axisId} value={a.axisId}>
                    {a.label} · {a.scope === "cross" ? "cross" : "vertical"}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                title="Inverter ordem"
                style={{
                  ...UI,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  border: `1px solid ${C.ink10}`,
                  cursor: "pointer",
                  borderRadius: 8,
                  background: C.white,
                  color: C.ink,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "7px 11px",
                }}
              >
                <ArrowDownUp size={15} />
                {sortDir === "asc" ? "Crescente" : "Decrescente"}
              </button>
              {selected.size > 0 && (
                <span style={{ ...UI, fontSize: 12, color: C.ink50, marginLeft: "auto" }}>
                  {selected.size} selecionado{selected.size === 1 ? "" : "s"}
                </span>
              )}
            </div>

            {/* Table */}
            <div
              style={{
                background: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: 16,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "32px minmax(200px,1.4fr) 120px minmax(160px,auto) minmax(160px,auto) 130px",
                  alignItems: "center",
                  columnGap: 10,
                  padding: "12px 18px",
                  background: "#FBFAFA",
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <span />
                {[
                  "Candidato",
                  "Nível",
                  "Competências Cross",
                  "Competências Verticais",
                  "Revisão",
                ].map((h) => (
                  <span
                    key={h}
                    style={{
                      ...UI,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: C.ink50,
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>

              {sorted.length === 0 ? (
                <div style={{ padding: 20 }}>
                  <Muted>
                    {data.set.status === "evaluating"
                      ? "Avaliando os candidatos… os resultados aparecem aqui conforme ficam prontos."
                      : "Nenhum candidato avaliado neste pacote."}
                  </Muted>
                </div>
              ) : (
                sorted.map((e) => {
                  const rev = reviewChipProps(e.reviewStatus);
                  return (
                    <div
                      key={e.evaluationId}
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "32px minmax(200px,1.4fr) 120px minmax(160px,auto) minmax(160px,auto) 130px",
                        alignItems: "center",
                        columnGap: 10,
                        padding: "12px 18px",
                        borderTop: `1px solid ${C.borderSoft}`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(e.evaluationId)}
                        onChange={() => toggleSelected(e.evaluationId)}
                        aria-label={`Selecionar ${e.candidateName}`}
                        style={{ cursor: "pointer" }}
                      />
                      {/* biome-ignore lint/a11y/noStaticElementInteractions: row click is a mouse convenience for the profile résumé; the keyboard path is the Revisar button in the same row */}
                      {/* biome-ignore lint/a11y/useKeyWithClickEvents: see above — keyboard users reach the same flows via the Revisar button */}
                      <div
                        onClick={() => setProfileEvalId(e.evaluationId)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          minWidth: 0,
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            flex: "none",
                            borderRadius: 10,
                            background: C.ink,
                            color: C.white,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            ...UI,
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          {initials(e.candidateName)}
                        </div>
                        <div
                          style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}
                        >
                          <span
                            style={{
                              ...DM,
                              fontWeight: 600,
                              fontSize: 15,
                              color: C.ink,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {e.candidateName}
                          </span>
                          {e.candidateSubtitle && (
                            <span
                              style={{
                                ...UI,
                                fontSize: 12,
                                color: C.ink50,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {e.candidateSubtitle}
                            </span>
                          )}
                        </div>
                      </div>
                      <LevelCell
                        level={e.overallLevel}
                        mean={e.overallMean}
                        stddev={e.overallStddev}
                        levels={data.levels}
                        names={data.levelNames}
                      />
                      <MiniBars axes={crossAxes} perAxis={e.perAxis} levels={data.levels} />
                      <MiniBars axes={vertAxes} perAxis={e.perAxis} levels={data.levels} />
                      <button
                        type="button"
                        onClick={() => setDrawerEvalId(e.evaluationId)}
                        style={{
                          ...UI,
                          justifySelf: "start",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          border: 0,
                          cursor: "pointer",
                          borderRadius: 999,
                          fontWeight: 600,
                          fontSize: 12.5,
                          padding: "6px 12px",
                          background: rev.bg,
                          color: rev.fg,
                        }}
                      >
                        {e.reviewStatus === "pending" ? "Revisar" : rev.label}
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{ ...UI, fontSize: 11.5, color: C.ink50, margin: "10px 2px 30px" }}>
              {`Nível geral e ${data.totalAxes} competências por candidato. `}
              Clique em um candidato para ver o resumo do perfil; use Revisar para avaliar
              competência a competência.
            </div>
          </>
        )}
      </div>

      {profileEvalId && data && (
        <ProfileDrawer
          evaluationId={profileEvalId}
          onClose={() => setProfileEvalId(null)}
          onReview={() => {
            // Bridge from the résumé into the competency review surface.
            setDrawerEvalId(profileEvalId);
            setProfileEvalId(null);
          }}
        />
      )}

      {drawerEvalId && data && (
        <CandidateDrawer
          evaluationId={drawerEvalId}
          setId={data.set.id}
          levelNames={data.levelNames}
          onClose={() => setDrawerEvalId(null)}
          onReviewed={() => void loadSet({ silent: true })}
        />
      )}
    </div>
  );
}
